import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

export interface MetricCardProps {
  icon: React.ReactElement<SvgIconComponent>;
  value: string | number;
  label: string;
  sublabel?: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  chip?: {
    label: string;
    color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default';
    variant?: 'filled' | 'outlined';
  };
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
  };
  tooltip?: string;
  onClick?: () => void;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  icon,
  value,
  label,
  sublabel,
  color = 'primary',
  chip,
  trend,
  tooltip,
  onClick,
}) => {
  const content = (
    <Box
      sx={{
        textAlign: 'center',
        p: 2,
        bgcolor: 'background.default',
        borderRadius: 1,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          bgcolor: 'action.hover',
          transform: 'translateY(-2px)',
          boxShadow: 2,
        } : {},
      }}
      onClick={onClick}
    >
      {React.cloneElement(icon, {
        sx: { fontSize: 36, color: `${color}.main`, mb: 1 },
      })}
      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {sublabel && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}
        >
          {sublabel}
        </Typography>
      )}
      {chip && (
        <Chip
          label={chip.label}
          size="small"
          color={chip.color || 'default'}
          variant={chip.variant || 'filled'}
          sx={{ mt: 0.5 }}
        />
      )}
      {trend && (
        <Typography
          variant="caption"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 0.5,
            color: trend.direction === 'up' ? 'success.main' :
                   trend.direction === 'down' ? 'error.main' : 'text.secondary',
          }}
        >
          {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}
          {' '}{Math.abs(trend.value)}%
        </Typography>
      )}
    </Box>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{content}</Tooltip>;
  }

  return content;
};

export default MetricCard;

